/**
 * Building blocks of the guides for brands and creators: the guide's opening over the aurora,
 * numbered steps along a timeline, and lists of points with a check mark. The docs use the
 * numbered steps too.
 */

import { Check } from "lucide-react";
import type { ReactNode } from "react";

import { Aurora, Eyebrow, typeScale } from "../../components/marketing.js";
import { Reveal } from "../../components/Reveal.js";
import { styles } from "../../components/ui.js";

/** A guide's opening: its eyebrow, title, introduction and actions, beside an example. */
export function GuideHero({
  eyebrow,
  title,
  documentTitle,
  actions,
  visual,
  children,
}: {
  eyebrow: string;
  title: string;
  documentTitle: string;
  actions: ReactNode;
  visual: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="relative isolate overflow-hidden">
      <title>{`${documentTitle} · Morcast`}</title>
      <Aurora />
      <div
        className={`${styles.frame} grid items-center gap-x-16 gap-y-12 pt-16 pb-20 sm:pt-24 sm:pb-24 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]`}
      >
        <Reveal>
          <Eyebrow>{eyebrow}</Eyebrow>
          <h1 className={`mt-7 ${typeScale.title}`}>{title}</h1>
          <p className={`mt-6 max-w-2xl ${typeScale.lead}`}>{children}</p>
          <div className="mt-10 flex flex-wrap gap-3">{actions}</div>
        </Reveal>
        <Reveal delay={150} className="mx-auto w-full max-w-md lg:max-w-none">
          {visual}
        </Reveal>
      </div>
    </section>
  );
}

/** Steps in order, each with a number, a title and what happens, joined by a line. */
export function GuideSteps({ steps }: { steps: { title: string; text: ReactNode }[] }) {
  return (
    <ol>
      {steps.map((step, index) => (
        <li
          key={step.title}
          className="relative flex gap-5 pb-8 last:pb-0 before:absolute before:top-12 before:bottom-1 before:left-[1.1875rem] before:w-px before:bg-gradient-to-b before:from-brand-500/40 before:to-zinc-900/[0.06] last:before:hidden dark:before:from-brand-300/25 dark:before:to-white/[0.05]"
        >
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand-300 to-brand-500 font-display text-sm font-semibold text-ink-950 shadow-[inset_0_1px_0_rgb(255_255_255/0.5),0_8px_24px_-8px_rgb(103_194_222/0.7)] dark:from-brand-400/[0.16] dark:to-brand-400/[0.05] dark:text-brand-200 dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.08)] dark:ring-1 dark:ring-brand-300/20 dark:ring-inset">
            {index + 1}
          </span>
          <div className="pt-1.5">
            <h3 className="font-display font-semibold tracking-[-0.01em] text-zinc-950 sm:text-lg dark:text-white">
              {step.title}
            </h3>
            <p className="mt-1.5 leading-7 text-zinc-600 dark:text-zinc-400">{step.text}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Points, each with a check mark. */
export function GuidePoints({ points }: { points: ReactNode[] }) {
  return (
    <ul className="space-y-3.5 text-zinc-700 dark:text-zinc-300">
      {points.map((point, index) => (
        // The points are fixed text that never reorders.
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed list
        <li key={index} className="flex gap-3">
          <span className="mt-1 grid size-5 shrink-0 place-items-center rounded-full bg-brand-500/10 text-brand-700 ring-1 ring-brand-600/25 ring-inset dark:bg-brand-400/15 dark:text-brand-300 dark:ring-brand-400/30">
            <Check aria-hidden className="size-3" strokeWidth={3} />
          </span>
          <span className="leading-7">{point}</span>
        </li>
      ))}
    </ul>
  );
}
